import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => getArticleBySlug(slug || ''), [slug]);
  const relatedArticles = useMemo(() => getRelatedArticles(slug || '', 4), [slug]);

  if (!article) {
    return (
      <div className="pt-20 pb-20 text-center">
        <p className="text-foreground-500">文章不存在</p>
        <Link
          to="/"
          className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://chiguawang.com/article/${article.slug}`,
    },
  };

  return (
    <article className="pt-20 pb-14">
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            {article.isHot && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-600">
                热门
              </span>
            )}
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
              >
                {category.name}
              </Link>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount.toLocaleString()} 阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line"></i>
              {article.tags.join(' · ')}
            </span>
          </div>
        </header>

        <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
            prose-p:mb-5 prose-p:leading-loose prose-p:text-foreground-700
            prose-strong:text-foreground-900 prose-strong:font-semibold
            prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-lg bg-secondary-50 text-secondary-700 text-sm hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-14">
            <SectionTitle title="相关文章" subtitle="你可能也对这些感兴趣" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}